import { useState } from "react";
import { useGlobalContext } from "../context/globalContext";
import { motion } from "framer-motion";
import { PlusCircle, Upload, Loader } from "lucide-react";

const CreateProduct: React.FC = () => {
  const { createProduct } = useGlobalContext();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    category: "",
    image: "",
    quantity: "",
  });

  // ✅ Handle input changes
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ✅ Handle image upload
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData((prev) => ({ ...prev, image: reader.result as string }));
    };
    reader.readAsDataURL(file);
  };

  // ✅ Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.image) {
      alert("Please upload an image before submitting.");
      return;
    }

    setLoading(true);
    try {
      await createProduct({
        ...formData,
        price: Number(formData.price),
        quantity: Number(formData.quantity),
        images: [formData.image],
      });

      // Reset form
      setFormData({
        name: "",
        description: "",
        price: "",
        category: "",
        image: "",
        quantity: "",
      });
    } catch (error) {
      console.error("Error creating product:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      className="bg-dark text-light shadow-lg rounded-4 p-4 mb-4 mx-auto"
      style={{ maxWidth: "600px" }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <h3 className="text-success fw-bold mb-4">
        <PlusCircle className="me-2" /> Create New Product
      </h3>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Product Name */}
        <div className="mb-3">
          <label htmlFor="name" className="form-label fw-semibold">
            Product Name
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="form-control bg-secondary text-light border-0"
            placeholder="e.g. Wooden Chair"
            required
          />
        </div>

        {/* Description */}
        <div className="mb-3">
          <label htmlFor="description" className="form-label fw-semibold">
            Description
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={3}
            className="form-control bg-secondary text-light border-0"
            placeholder="Brief product description..."
            required
          />
        </div>

        {/* Price */}
        <div className="mb-3">
          <label htmlFor="price" className="form-label fw-semibold">
            Price (₦)
          </label>
          <input
            type="number"
            id="price"
            name="price"
            value={formData.price}
            onChange={handleChange}
            className="form-control bg-secondary text-light border-0"
            placeholder="e.g. 25000"
            required
          />
        </div>

        {/* Quantity */}
        <div className="mb-3">
          <label htmlFor="quantity" className="form-label fw-semibold">
            Quantity
          </label>
          <input
            type="number"
            id="quantity"
            name="quantity"
            value={formData.quantity}
            onChange={handleChange}
            className="form-control bg-secondary text-light border-0"
            placeholder="e.g. 10"
            required
          />
        </div>

        {/* Category (Text Input instead of Dropdown) */}
        <div className="mb-3">
          <label htmlFor="category" className="form-label fw-semibold">
            Category
          </label>
          <input
            type="text"
            id="category"
            name="category"
            value={formData.category}
            onChange={handleChange}
            className="form-control bg-secondary text-light border-0"
            placeholder="e.g. Office Furniture"
            required
          />
        </div>

        {/* Image Upload */}
        <div className="mb-3">
          <label className="form-label fw-semibold d-block">Product Image</label>
          <input
            type="file"
            id="image"
            accept="image/*"
            className="d-none"
            onChange={handleImageChange}
          />
          <label
            htmlFor="image"
            className="btn btn-outline-success d-flex align-items-center"
            style={{ width: "fit-content" }}
          >
            <Upload className="me-2" size={18} />
            Upload Image
          </label>

          {formData.image && (
            <div className="mt-3 text-center">
              <img
                src={formData.image}
                alt="Preview"
                className="rounded shadow-sm"
                style={{ maxWidth: "120px", maxHeight: "120px", objectFit: "cover" }}
              />
              <p className="text-muted small mt-2">Image preview</p>
            </div>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          className="btn btn-success w-100 py-2 fw-semibold"
          disabled={loading}
        >
          {loading ? (
            <>
              <Loader className="me-2 animate-spin" size={18} /> Creating Product...
            </>
          ) : (
            <>
              <PlusCircle className="me-2" size={18} /> Create Product
            </>
          )}
        </button>
      </form>
    </motion.div>
  );
};

export default CreateProduct;
